// ─── Feuille : ÉVÉNEMENTS DU JOUR sélectionné ─────────────────────
// Extrait d'EcranCalendrier (découpage). Liste antéchronologique du jour :
// swipe ✏️/⇄/🗑️(+série), tap = fiche info, ＋ en-tête = ajout. Les actions
// remontent au parent (navigation entre modales inchangée).
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, LigneSwipe, useUI, useT, frJM } from '@mop/core';
import { emojiCategorie } from './data/categories';

const COL_FERIE = '#EF5350';   // rouge férié (fidèle MoPCal)

export default function FeuilleEvenementsJour({
  visible, titre, edit, ferieNomJour, eventsJour, regleCourte, dureeCourte, urlItineraire, ouvrirItineraire,
  onFermer, onAjouter, onEditer, onSupprimer, onSupprimerSerie, onDeplacer, onOuvrirFiche,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  return (
    <Feuille visible={visible} titre={titre} emoji="📅" hauteur={0.82}
      onFermer={onFermer}
      action={edit ? { icone: '＋', onPress: onAjouter, label: t('calendrier.ajouterEvenement') } : undefined}>
      {!!ferieNomJour && <Text style={s.ferieTxt}>{t('calendrier.ferieLigne', { nom: ferieNomJour })}</Text>}
      {eventsJour.length === 0 ? (
        <Text style={s.vide}>{t('calendrier.aucunEvenementPoint')}{edit ? t('calendrier.toucherPlusAjouter') : ''}</Text>
      ) : eventsJour.map(ev => {
        // Une PLAGE se supprime et s'édite comme un événement simple (les
        // actions visent l'événement d'origine) ; seul l'EDT reste intouchable
        // ici, et le déplacement d'occurrence n'a pas de sens sur une plage.
        const simple = !ev.isRecurrent && !ev.isEdt;
        // Colonne horaire : DÉBUT au-dessus de la FIN, toujours affichés.
        // Plage multi-jours : date courte JJ/MM devant l'heure quand le début
        // (ou la fin) tombe un autre jour — même règle que la grille horaire.
        let deb = ev.heure || '', fin = ev.heureFin || '';
        if (ev.estPlage) {
          deb = ev.plageRole === 'debut'
            ? (ev.plageHeureDebut || '')
            : `${frJM(ev.plageSourceDk)}${ev.plageHeureDebut ? ` ${ev.plageHeureDebut}` : ''}`;
          fin = ev.plageRole === 'fin'
            ? (ev.plageHeureFin || '')
            : `${frJM(ev.dateFin)}${ev.plageHeureFin ? ` ${ev.plageHeureFin}` : ''}`;
        }
        return (
          <LigneSwipe key={ev.id} espacement={7} enabled={edit && (simple || ev.isRecurrent)}
            onSupprimer={edit && (simple || ev.isRecurrent) ? () => onSupprimer(ev) : undefined}
            onSupprimerSerie={edit && ev.isRecurrent ? () => onSupprimerSerie(ev) : undefined}
            onEditer={edit && (simple || ev.isRecurrent) ? () => onEditer(ev) : undefined}
            onDeplacer={edit && (ev.isRecurrent || (simple && !ev.dateFin)) ? () => onDeplacer(ev) : undefined}>
            <TouchableOpacity style={s.eve} activeOpacity={0.7} onPress={() => onOuvrirFiche(ev)}>
              <View style={[s.eveBarre, { backgroundColor: ev._couleur || UI.accent }]} />
              <View style={s.eveHeures}>
                <Text style={[s.eveHeure, ev.estPlage && ev.plageRole !== 'debut' && s.eveHeureDate]} numberOfLines={1}>{deb || '—'}</Text>
                {!!fin && <Text style={[s.eveHeure, s.eveHeureFin, ev.estPlage && ev.plageRole !== 'fin' && s.eveHeureDate]} numberOfLines={1}>{fin}</Text>}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.eveTitre} numberOfLines={1}>{ev.categorie ? emojiCategorie(ev.categorie) + ' ' : ''}{ev.isRecurrent ? '🔁 ' : ''}{ev.titre}{ev.pieces?.length ? `  📎${ev.pieces.length}` : ''}</Text>
                {!!ev.note && <Text style={s.eveNote} numberOfLines={2}>{ev.note}</Text>}
                {!!ev.lieu && (
                  <TouchableOpacity onPress={() => ouvrirItineraire?.(ev)} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
                    <Text style={s.eveLieu} numberOfLines={1}>📍 {ev.lieu}  🧭</Text>
                  </TouchableOpacity>
                )}
              </View>
              {/* Colonne droite : règle de récurrence (compacte) + durée. */}
              {(() => {
                const regle = ev.isRecurrent ? regleCourte(ev.recDef) : '';
                const dur = !ev.estPlage ? dureeCourte(ev.heure, ev.heureFin) : '';
                if (!regle && !dur) return null;
                return (
                  <View style={s.eveDroite}>
                    {!!regle && <Text style={s.eveRegle} numberOfLines={1}>{regle}</Text>}
                    {!!dur && <Text style={s.eveDuree}>{dur}</Text>}
                  </View>
                );
              })()}
            </TouchableOpacity>
          </LigneSwipe>
        );
      })}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ferieTxt: { fontSize: 12.5, color: COL_FERIE, fontWeight: '700', marginBottom: 8 },
  vide: { fontSize: 13, color: UI.faint, fontStyle: 'italic' },
  eve: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 11, borderWidth: 1, borderColor: UI.border, padding: 12 },
  eveBarre: { width: 4, alignSelf: 'stretch', borderRadius: 2 },
  eveHeure: { fontSize: 13, fontWeight: '800', color: UI.accent },
  eveHeures: { minWidth: 48, alignItems: 'flex-start', gap: 1 },
  eveHeureFin: { opacity: 0.75 },
  eveHeureDate: { fontSize: 10.5 },   // « JJ/MM HH:MM » (début/fin sur un autre jour)
  eveTitre: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  eveNote: { fontSize: 12, color: UI.muted, marginTop: 1 },
  eveLieu: { fontSize: 12, color: UI.accent, fontWeight: '600', marginTop: 2 },
  eveDroite: { alignItems: 'flex-end', marginLeft: 8, maxWidth: 130 },
  eveRegle: { fontSize: 10.5, color: UI.muted, fontWeight: '700' },
  eveDuree: { fontSize: 10.5, color: UI.faint, marginTop: 2 },
});
